<template>
    <VeeForm @submit="saveCertificate" v-slot="{ isSubmitting }">
        <div class="flex flex-column gap-5">
            <div class="flex flex-column gap-3 w-full align-items-center">

                <CustomSelectOneSeason
                    name="season" v-model="newCertificate.seasonId"
                    :label="$t('message.horseEdit.covering.season')"
                    class="w-8"
                    noPlaceholder
                    required
                    rules="required"
                    showButtonBar/>

                <CustomInputText
                    name="agreementNumber"
                    class="w-8"
                    :label="$t('message.horseEdit.covering.number')"
                    noPlaceholder
                    v-model="newCertificate.agreementNumber"
                    rules="required"
                    @input="inputFormat('agreementNumber')"
                    @keypress="onlyAllowNumbers"
                    required
                />

                <CustomInputText
                    name="mareName"
                    disabled
                    class="w-8"
                    :label="$t('message.horseEdit.covering.mare')"
                    noPlaceholder
                    v-model="newCertificate.mareName"
                />

                <CustomSelectOneCity name="location" :label="$t('message.horseEdit.covering.location')"
                                     class="w-8" noPlaceholder v-model="newCertificate.location" required
                                     rules="required"
                                     :disabled="internalRenew" filter />

                <CustomSelectOneCoveringType
                    name="coveringType"
                    class="w-8"
                    :label="$t('message.horseEdit.covering.coveringType')"
                    noPlaceholder
                    v-model="newCertificate.coveringType"
                    rules="required"
                    required
                />

                <CustomSelectOneSemenType
                    v-if="showSemenInput"
                    name="semenType"
                    class="w-8"
                    :label="$t('message.horseEdit.covering.semenType')"
                    noPlaceholder
                    v-model="newCertificate.semenType"
                    rules="required"
                    required
                />

                <CustomCalendar name="firstCoveringDate" :label="$t('message.horseEdit.covering.firstCovering')"
                                class="w-8" noPlaceholder v-model="newCertificate.firstCoveringDate" required
                                rules="required"/>

                <CustomCalendar name="lastCoveringDate" :label="$t('message.horseEdit.covering.lastCovering')"
                                class="w-8" noPlaceholder v-model="newCertificate.lastCoveringDate" required
                                :rules="{ required: true,isBefore: getStartDate }"
                />

                <CustomSelectOneSemenOrigin
                    v-if="showOriginInput && showSemenInput"
                    name="semenOrigin"
                    class="w-8"
                    :label="$t('message.horseEdit.covering.semenOrigin')"
                    noPlaceholder
                    v-model="newCertificate.semenOrigin"
                    rules="required"
                    required
                />
            </div>

            <div class="flex justify-content-center">
                <CustomButton
                    type="submit"
                    :label="$t('message.horseEdit.covering.addCertificate')"
                    :disabled="isSubmitting"
                />
            </div>
        </div>
    </VeeForm>
</template>

<script>
import _ from "lodash";
import Dialog from "primevue/dialog";
import { Form as VeeForm } from "vee-validate";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
import CustomInputText from "@/components/form/CustomInputText.vue";
import CustomSelectOne from "@/components/form/inner/CustomSelectOne.vue";
import CustomButton from "@/components/form/CustomButton.vue";
import {
    createCoveringCertificate,
} from "@/swagger/vue-api-client";
import CustomSelectOneCoveringType from "@/components/form/CustomSelectOneCoveringType.vue";
import CustomInputTextArea from "@/components/form/CustomInputTextArea.vue";
import CustomSelectOneSemenType from "@/components/form/CustomSelectOneSemenType.vue";
import CustomSelectOneSemenOrigin from "@/components/form/CustomSelectOneSemenOrigin.vue";
import CustomSelectOneAgreementType from "@/components/form/CustomSelectOneAgreementType.vue";
import CustomSelectOneSeason from "@/components/form/CustomSelectOneSeason.vue";
import CustomSelectOneCity from "@/components/form/CustomSelectOneCity.vue";

export default {
    name: "NewCertificateDialogAgrementTab",

    components: {
        CustomSelectOneSeason,
        CustomSelectOneAgreementType,
        CustomSelectOne,
        CustomInputText,
        CustomInputNumber,
        Dialog,
        CustomButton,
        CustomSelectOneCoveringType,
        CustomSelectOneSemenType,
        CustomSelectOneSemenOrigin,
        CustomInputTextArea,
        CustomCalendar,
        VeeForm,
        CustomSelectOneCity,
    },

    props: {
        modelValue: null,
        horseName: {
            type: String,
            default: "",
        },
    },

    emits: ["search", "update:modelValue", "refreshHorse"],

    data() {
        return {
            newCertificate: this.getClearNewCertificate(),
            showSemenInput: false,
            showOriginInput: false,
        };
    },

    methods: {
        getStartDate() {
            return this.newCertificate.firstCoveringDate;
        },
        inputFormat(inputValue) {
            // Get the current value and remove any non-numeric characters
            let value = this.newCertificate[inputValue].replace(/\D/g, "");

            value = this.newCertificate[inputValue].replace(/^0+/, "");

            if (value.length < 6) {
                value = value.padStart(6, "0");
            }

            if (value.length > 6) {
                value = value.slice(0, 6);
            }

            this.newCertificate[inputValue] = value;
        },

        onlyAllowNumbers(e) {
            const charCode = e.which ? e.which : e.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                e.preventDefault();
                return false;
            }
            return true;
        },

        async saveCertificate() {
            try {
                const newCertificateRequest = _.cloneDeep(this.newCertificate);

                await createCoveringCertificate({body: newCertificateRequest});

                this.$emit("search");
                this.$emit("refreshHorse");
                this.newCertificate = this.getClearNewCertificate();
                this.internalValue = false;
                this.notifySaved();
            } catch (err) {
                this.notifyError(err);
            }
        },

        getClearNewCertificate() {
            return {
                agreementNumber: null,
                mareId: this.$route.params.id,
                mareName: this.horseName,
                coveringType: null,
                semenType: null,
                semenOrigin: null,
                firstCoveringDate: null,
                lastCoveringDate: null,
                seasonId: null,
                location: null,
            };
        },

        notifySaved() {
            this.$toast.add({
                severity: "success",
                summary: this.$t("message.other.success"),
                detail: this.$t("message.other.dataSaved"),
                life: 3000,
            });
        },

        notifyError(error) {
            const message = error.response?.data?.message || this.$t("unknownError");
            this.$toast.add({
                severity: "error",
                summary: this.$t("message.other.error"),
                detail: message,
                life: 5000,
            });
        },
    },

        computed: {
            internalValue: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    this.$emit("update:modelValue", value);
                },
            },
        },

        watch: {
            "newCertificate.coveringType": function (val) {
                if (val === "ARTIFICIAL_INSEMINATION") {
                    this.showSemenInput = true;
                } else {
                    this.showSemenInput = false;
                    this.newCertificate.semenType = null;
                    this.newCertificate.semenOrigin = null;
                }
            },
            "newCertificate.semenType": function (val) {
                if (val === "FROZEN") {
                    this.showOriginInput = true;
                } else {
                    this.showOriginInput = false;
                    this.newCertificate.semenOrigin = null;
                }
            },
        },
    };
</script>

<style lang="scss">
</style>
